import { trace } from "firebase/performance";
import {
    createContext,
    PropsWithChildren,
    useContext,
    useMemo,
    useState
} from "react";
import { useTranslation } from "react-i18next";

import {
    requireOneOfStates,
    requireState,
    requireOneOfSubStates,
    requireSubState,
    WrapRootState
} from "./Common";
import {
    loadMoreOrders,
    scanOperatorBadge,
    showConfiguration,
    showHardware,
    showInsights,
    showOrders,
    showSystemInformation
} from "./Operator";
import {
    CartClient,
    getEventPayload,
    SessionClient
} from "../../clients/CartClient";
import { ConfigClient } from "../../clients/ConfigClient";
import { LogClient } from "../../clients/LogClient";
import { OrderClient } from "../../clients/OrderClient";
import { PostCheckoutClient } from "../../clients/PostCheckoutClient";
import { getPerformanceInstance } from "../../firebase";
import { MrzParseError, mrzToDocumentId } from "../../mrzParse";
import { usePrinter } from "../../Printer/printer";
import { Tracker } from "../../track";
import {
    Action,
    ActionKind,
    ManuallyAddedProduct,
    ScannedCode
} from "../../types/Action";
import {
    Cart,
    CartError,
    CheckoutError,
    AddProductError,
    ApplyCouponError,
    VerifyAgeWithIdentityDocumentError,
    KitchenOrder,
    StartError,
    UpdateLineItemError,
    DeleteLineItemError,
    Session
} from "../../types/Cart";
import { KitchenProduct, KitchenMenuConfig } from "../../types/KitchenMenu";
import { Operator, OperatorSubStateKind } from "../../types/Operator";
import { ProductPreset } from "../../types/ProductPreset";
import { Wrap } from "../../types/Wrap";
import { hasUnappliedCoupon } from "../../utils/Cart";
import { setCartId } from "../../utils/cartId";
import { httpClient } from "../../utils/http";
import { getEzioId } from "../../utils/id";
import { getSimulatePaymentResponse } from "../../utils/simulate";
import { Button } from "../Button";
import { useConfig } from "../ConfigProvider";
import { debounceWithId } from "../debounce";
import { isValidOperatorBadge } from "../hooks/useBarcodeScanner";
import { logMemoryUsage } from "../logMemoryUsage";
import * as svg from "../svg";
import { ToastMessage } from "../ToastMessage";

export const useRootState = <T extends RootState["kind"]>(expectedState: T) => {
    const { state, dispatch } = useContext(StateContext);

    return { state: requireState(state, expectedState), dispatch };
};

export const useOneOfRootStates = <T extends RootState["kind"]>(
    ...expectedStates: T[]
) => {
    const { state, dispatch } = useContext(StateContext);

    return { state: requireOneOfStates(state, ...expectedStates), dispatch };
};

export const useRootAndSubState = <
    U extends RootState,
    T extends U["kind"],
    R extends U["subState"]["kind"]
>(
    expectedState: T,
    expectedSubState: R
) => {
    const { state, dispatch } = useContext(StateContext);

    return {
        state: requireSubState(state, expectedState, expectedSubState),
        dispatch
    };
};

export const useAnyState = () => {
    return useContext(StateContext);
};

export enum RootStateKind {
    OutOfOrder = "outOfOrder",
    Welcome = "welcome",
    Scan = "scan",
    CheckoutSuccess = "checkoutSuccess",
    Flappy = "flappy",
    Error = "error",
    Operator = "operator"
}

export enum OutOfOrderSubStateKind {
    Idle = "idle",
    OperatorVerification = "operatorVerification"
}

export enum WelcomeSubStateKind {
    Error = "error",
    Idle = "idle",
    Loading = "loading",
    OperatorVerification = "operatorVerification",
    Resetting = "resetting"
}

export enum ScanSubStateKind {
    Idle = "idle",
    Loading = "loading",
    CheckingOut = "checkingOut",
    UnappliedCoupon = "unappliedCoupon",
    ProcessPayment = "processPayment",
    ProductPicker = "productPicker",
    AgeVerification = "ageVerification",
    CartCheck = "cartCheck",
    Kitchen = "kitchen",
    CheckoutError = "checkoutError",
    Error = "error"
}

export enum CheckoutSuccessSubStateKind {
    Idle = "idle",
    Loading = "loading",
    ReceiptLoading = "receiptLoading",
    ReceiptSuccess = "receiptSuccess"
}

export enum FlappySubStateKind {
    Playing = "playing"
}

export type OutOfOrder = WrapRootState<
    RootStateKind.OutOfOrder,
    {
        subState:
            | Wrap<OutOfOrderSubStateKind.Idle>
            | Wrap<OutOfOrderSubStateKind.OperatorVerification>;
    }
>;

export type Welcome = WrapRootState<
    RootStateKind.Welcome,
    {
        subState: Wrap<
            | WelcomeSubStateKind.Idle
            | WelcomeSubStateKind.Loading
            | WelcomeSubStateKind.OperatorVerification
            | WelcomeSubStateKind.Resetting
        >;
    }
>;

export type AgeVerification = Wrap<
    ScanSubStateKind.AgeVerification,
    {
        status: "unconfirmed" | "success" | "error" | "underage" | "invalidId";
        attemptedAction: ManuallyAddedProduct | ScannedCode;
    }
>;

export type Kitchen = Wrap<
    ScanSubStateKind.Kitchen,
    {
        subState:
            | Wrap<"idle" | "loading">
            | Wrap<"productSelector", { product: KitchenProduct }>
            | Wrap<"error", { reason: CartError }>;
    }
>;

export type Scan = WrapRootState<
    RootStateKind.Scan,
    {
        kitchen: KitchenMenuConfig;
        cart: Cart;
        simulatePaymentResponse?:
            | "success"
            | "success-raw"
            | "failure"
            | "aborted";
        subState:
            | Wrap<
                  | ScanSubStateKind.Idle
                  | ScanSubStateKind.Loading
                  | ScanSubStateKind.CheckingOut
                  | ScanSubStateKind.UnappliedCoupon
                  | ScanSubStateKind.ProcessPayment
                  | ScanSubStateKind.CartCheck
              >
            | Wrap<
                  ScanSubStateKind.ProductPicker,
                  { productPreset: ProductPreset }
              >
            | Kitchen
            | AgeVerification
            | Wrap<ScanSubStateKind.CheckoutError, { reason: CheckoutError }>
            | Wrap<ScanSubStateKind.Error, { reason: CartError }>;
    }
>;

export type CheckoutSuccess = WrapRootState<
    RootStateKind.CheckoutSuccess,
    {
        orderId: string;
        userId?: string;
        kitchenOrder?: KitchenOrder;
        subState:
            | Wrap<
                  | CheckoutSuccessSubStateKind.Idle
                  | CheckoutSuccessSubStateKind.Loading
                  | CheckoutSuccessSubStateKind.ReceiptLoading
              >
            | Wrap<
                  CheckoutSuccessSubStateKind.ReceiptSuccess,
                  { receiptUrl: string }
              >;
    }
>;

export type Flappy = WrapRootState<
    RootStateKind.Flappy,
    { subState: Wrap<FlappySubStateKind.Playing> }
>;

export type Error = WrapRootState<RootStateKind.Error, { subState: Wrap<""> }>;

export type RootState =
    | OutOfOrder
    | Welcome
    | Scan
    | CheckoutSuccess
    | Flappy
    | Operator
    | Error;

export type State = {
    dispatch: (action: Action) => Promise<void | boolean>;
    state: RootState;
};

const germanyCountryCode = "DE";

const outOfOrderRootState: RootState = {
    kind: RootStateKind.OutOfOrder,
    subState: { kind: OutOfOrderSubStateKind.Idle },
    accessibilityMode: false,
    messages: []
};
const welcomeRootState: RootState = {
    kind: RootStateKind.Welcome,
    subState: { kind: WelcomeSubStateKind.Idle },
    accessibilityMode: false,
    messages: []
};

export const StateContext = createContext<State>({
    dispatch: (_: Action) => Promise.resolve(),
    state: welcomeRootState
});

export const StateProvider = ({ children }: PropsWithChildren) => {
    const client = httpClient;
    const { isEnabled, store } = useConfig();

    const { i18n, t } = useTranslation();

    const defaultRootState: RootState = isEnabled
        ? welcomeRootState
        : outOfOrderRootState;

    const [state, setState] = useState<RootState>(defaultRootState);

    const printer = usePrinter();
    const { connected, connect, print } = printer;

    const tracker = useMemo(() => new Tracker(), []);

    const defaultLanguage = i18n.options.fallbackLng as string;

    const reset = async () => {
        setState(defaultRootState);

        tracker.resetTrackingProperties();
        await i18n.changeLanguage(defaultLanguage);
    };

    const accessibilityModeAvailable = ![
        RootStateKind.Flappy,
        RootStateKind.Error
    ].includes(state.kind);

    const actionHandler = (client: typeof fetch) => async (action: Action) => {
        const cartClient = new CartClient(client);
        const sessionClient = new SessionClient(client);
        const orderClient = new OrderClient(client);
        const postCheckoutClient = new PostCheckoutClient(client, tracker);
        const logClient = new LogClient(client);
        const configClient = new ConfigClient(client);

        switch (action.kind) {
            case ActionKind.Start: {
                const baseState = requireOneOfStates(
                    state,
                    RootStateKind.Welcome,
                    RootStateKind.CheckoutSuccess
                );

                // Chromium-only feature that gives us memory usage insights.
                if (window.performance?.memory) {
                    logMemoryUsage(logClient, window.performance.memory);
                }

                if (baseState.kind === RootStateKind.Welcome) {
                    if (
                        baseState.subState.kind === WelcomeSubStateKind.Loading
                    ) {
                        await logClient.info(
                            "Tried starting while on loading state"
                        );
                        return;
                    }
                    setState({
                        ...baseState,
                        subState: { kind: WelcomeSubStateKind.Loading }
                    });
                } else {
                    if (
                        baseState.subState.kind ===
                        CheckoutSuccessSubStateKind.Loading
                    ) {
                        return;
                    }
                    setState({
                        ...baseState,
                        subState: { kind: CheckoutSuccessSubStateKind.Loading }
                    });
                }

                const { accessibilityMode, messages } = baseState;

                try {
                    const session: Session = await sessionClient.checkin();
                    let cart = session.cart;

                    setCartId(cart.id.toString());
                    tracker.setTrackingProperties({
                        cartId: cart.id,
                        language: i18n.language
                    });

                    const subState: Scan["subState"] =
                        action.openKitchen &&
                        session.sessionConfiguration.kitchen.hasKitchen &&
                        !!session.sessionConfiguration.kitchen.menu
                            ? {
                                  kind: ScanSubStateKind.Kitchen,
                                  subState: { kind: "idle" as const }
                              }
                            : {
                                  kind: ScanSubStateKind.Idle
                              };

                    const newState: Scan = {
                        accessibilityMode,
                        messages,
                        kitchen: session.sessionConfiguration.kitchen,
                        kind: RootStateKind.Scan,
                        cart,
                        simulatePaymentResponse: getSimulatePaymentResponse(),
                        subState
                    };

                    setState(newState);
                    tracker.track("session_start");
                    tracker.track("page_view", { title: "scan" });

                    if (action.scannedCode) {
                        const eventPayload = {
                            barcode: action.scannedCode.code,
                            quantity: 1,
                            source: "scan",
                            device: action.scannedCode.device
                        };

                        try {
                            cart = await cartClient.add([
                                { barcode: action.scannedCode.code }
                            ]);
                            setState({ ...newState, cart });
                        } catch (e) {
                            const error = e as AddProductError;
                            const state: RootState = (() => {
                                switch (error.case) {
                                    case "IdScanRequired":
                                        tracker.track(
                                            "age_verification_start",
                                            eventPayload
                                        );

                                        return {
                                            ...newState,
                                            subState: {
                                                kind: ScanSubStateKind.AgeVerification,
                                                status: "unconfirmed",
                                                attemptedAction:
                                                    action.scannedCode
                                            }
                                        };
                                    case "ProductNotFound":
                                        tracker.track(
                                            "product_not_found",
                                            eventPayload
                                        );

                                        return {
                                            ...newState,
                                            subState: {
                                                kind: ScanSubStateKind.Error,
                                                reason: error
                                            }
                                        };
                                    case "AgeRequirementNotMet":
                                        tracker.track(
                                            "age_requirement_not_met",
                                            eventPayload
                                        );

                                        return {
                                            ...newState,
                                            subState: {
                                                kind: ScanSubStateKind.AgeVerification,
                                                status: "underage" as const,
                                                attemptedAction:
                                                    action.scannedCode
                                            }
                                        };
                                    case "SCODisabled":
                                        return {
                                            ...newState,
                                            kind: RootStateKind.OutOfOrder,
                                            subState: {
                                                kind: OutOfOrderSubStateKind.Idle
                                            }
                                        };
                                    case "Unknown":
                                    case "CouponAlreadyRedeemed":
                                    case "ServiceNotSellable":
                                    case "CouponNotApplicable": // TEMP basel-opening
                                        return {
                                            ...newState,
                                            subState: {
                                                kind: ScanSubStateKind.Error,
                                                reason: error
                                            }
                                        };
                                    default:
                                        return error satisfies never;
                                }
                            })();

                            setState(state);
                            return;
                        }
                    }
                    return;
                } catch (e) {
                    const error = e as StartError;
                    switch (error.case) {
                        case "UnknownRunningVersion":
                        case "DeviceDisabled":
                            return setState({
                                accessibilityMode,
                                messages,
                                kind: RootStateKind.OutOfOrder,
                                subState: {
                                    kind: OutOfOrderSubStateKind.Idle
                                }
                            });
                        default:
                            throw error;
                    }
                }
            }

            case ActionKind.AddProduct: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Loading }
                });

                const eventPayload = getEventPayload(action);
                try {
                    const cart = await cartClient.add([
                        {
                            barcode: action.payload.code,
                            quantity: 1
                        }
                    ]);
                    setState({
                        ...scanState,
                        cart,
                        subState: { kind: ScanSubStateKind.Idle }
                    });

                    tracker.track("add_to_cart", eventPayload);
                } catch (e) {
                    const error = e as AddProductError;
                    const state: RootState = (() => {
                        switch (error.case) {
                            case "ProductNotFound":
                                tracker.track(
                                    "product_not_found",
                                    eventPayload
                                );

                                return {
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.Error,
                                        reason: error
                                    }
                                };
                            case "IdScanRequired":
                                tracker.track(
                                    "age_verification_start",
                                    eventPayload
                                );

                                return {
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.AgeVerification,
                                        status: "unconfirmed" as const,
                                        attemptedAction: action.payload
                                    }
                                };
                            case "AgeRequirementNotMet":
                                tracker.track(
                                    "age_requirement_not_met",
                                    eventPayload
                                );

                                return {
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.AgeVerification,
                                        status: "underage" as const,
                                        attemptedAction: action.payload
                                    }
                                };
                            case "SCODisabled":
                                return {
                                    ...scanState,
                                    kind: RootStateKind.OutOfOrder,
                                    subState: {
                                        kind: OutOfOrderSubStateKind.Idle
                                    }
                                };
                            case "Unknown":
                            case "CouponAlreadyRedeemed":
                            case "ServiceNotSellable":
                            case "CouponNotApplicable": // TEMP basel-opening
                                return {
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.Error,
                                        reason: error
                                    }
                                };
                            default:
                                return error satisfies never;
                        }
                    })();

                    setState(state);
                    return;
                }

                return;
            }

            case ActionKind.ApplyCoupon: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Loading }
                });

                const eventPayload = { code: action.code };

                try {
                    const cart = await cartClient.applyCoupon(action.code);

                    setState({
                        ...scanState,
                        kind: RootStateKind.Scan,
                        cart,
                        subState: { kind: ScanSubStateKind.Idle }
                    });

                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage message={t("couponApplied")} />
                            ),
                            timeoutMs: 1500,
                            type: "toast"
                        }
                    });

                    tracker.track("apply_coupon", eventPayload);
                } catch (e) {
                    const error = e as ApplyCouponError;
                    const state: RootState = (() => {
                        switch (error.case) {
                            case "ApplyCouponError":
                                tracker.track(
                                    "apply_coupon_failure",
                                    eventPayload
                                );

                                return {
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.Error,
                                        reason: { case: "ApplyCouponError" }
                                    }
                                };
                            case "SCODisabled":
                                return {
                                    ...scanState,
                                    kind: RootStateKind.OutOfOrder,
                                    subState: {
                                        kind: OutOfOrderSubStateKind.Idle
                                    }
                                };
                            case "Unknown":
                                return {
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.Error,
                                        reason: error
                                    }
                                };
                            default:
                                return error satisfies never;
                        }
                    })();

                    setState(state);
                }

                return;
            }

            case ActionKind.ScanId: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Loading }
                });

                try {
                    await logClient.info(
                        `ID scan attempted, received this code from the scanner: '${action.identityDocumentScan.code}'`
                    );

                    if (isValidOperatorBadge(action.identityDocumentScan)) {
                        await cartClient.skipAgeVerification();
                    } else {
                        const identityDocument = mrzToDocumentId(
                            action.identityDocumentScan.code
                        );

                        await cartClient.scanId(identityDocument);
                    }

                    tracker.track("id_scan_success", {
                        device: action.identityDocumentScan.device
                    });
                    setState({
                        ...scanState,
                        subState: {
                            kind: ScanSubStateKind.AgeVerification,
                            status: "success",
                            attemptedAction: action.attemptedAction
                        }
                    });
                } catch (e) {
                    if ((e as MrzParseError) === "UnrecognizedMrz") {
                        setState({
                            ...scanState,
                            subState: {
                                kind: ScanSubStateKind.AgeVerification,
                                status: "invalidId",
                                attemptedAction: action.attemptedAction
                            }
                        });
                    } else {
                        const error = e as VerifyAgeWithIdentityDocumentError;
                        let status;

                        switch (error.case) {
                            case "Underage":
                                status = "underage" as const;

                                setState({
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.AgeVerification,
                                        status: status,
                                        attemptedAction: action.attemptedAction
                                    }
                                });

                                break;
                            case "SCODisabled":
                                status = "SCODisabled" as const;

                                setState({
                                    ...scanState,
                                    kind: RootStateKind.OutOfOrder,
                                    subState: {
                                        kind: OutOfOrderSubStateKind.Idle
                                    }
                                });

                                break;
                            case "Unknown":
                                setState({
                                    ...scanState,
                                    subState: {
                                        kind: ScanSubStateKind.AgeVerification,
                                        status: "error",
                                        attemptedAction: action.attemptedAction
                                    }
                                });

                                break;
                            default:
                                return error satisfies never;
                        }

                        tracker.track("id_scan_failure", {
                            reason: status,
                            device: action.identityDocumentScan.device
                        });
                    }
                }

                return;
            }

            case ActionKind.UpdateLineItem: {
                const scanState = requireState(state, RootStateKind.Scan);

                const t = trace(getPerformanceInstance(), "updateLineItem");

                // We're about to perform an optimistic visual update. Quantity buttons are not available for grouped entries.
                // This means visually updating *only* the ungrouped line item, but submitting the total quantity across all line items to the API.
                const currentSingleQuantity = scanState.cart.positions.reduce(
                    (acc, cur) =>
                        acc +
                        (cur.kind === "single" && cur.id === action.product.id
                            ? cur.quantity
                            : 0),
                    0
                );
                const newSingleQuantity =
                    action.product.action === "increment"
                        ? currentSingleQuantity + 1
                        : currentSingleQuantity - 1;

                let cart: Cart = (() => {
                    console.log(
                        "Optimistically updating cart line item",
                        action.product.id,
                        "with new quantity",
                        newSingleQuantity
                    );

                    const newPositions = scanState.cart.positions.map((p) => {
                        if (p.kind === "single" && p.id === action.product.id)
                            return {
                                ...p,
                                quantity: newSingleQuantity
                            };
                        return p;
                    });

                    return { ...scanState.cart, positions: newPositions };
                })();

                setState((prev) => ({
                    ...(prev as Scan),
                    cart,
                    subState: { kind: ScanSubStateKind.Idle }
                }));

                tracker.track("update_line_item", { ...action.product });

                debounceWithId(async () => {
                    const newTotalQuantity =
                        newSingleQuantity +
                        scanState.cart.positions.filter(
                            (p) =>
                                p.kind === "group" &&
                                p.lineItems.some(
                                    (li) => li.id === action.product.id
                                )
                        ).length;

                    t.start();

                    try {
                        cart = await cartClient.updateLineItems([
                            {
                                id: action.product.id,
                                quantity: newTotalQuantity
                            }
                        ]);

                        t.stop();

                        setState((prev) => ({
                            ...(prev as Scan),
                            cart,
                            subState: { kind: ScanSubStateKind.Idle }
                        }));
                    } catch (e) {
                        const error = e as UpdateLineItemError;
                        switch (error.case) {
                            case "SCODisabled":
                                setState((prev) => ({
                                    ...prev,
                                    kind: RootStateKind.OutOfOrder,
                                    subState: {
                                        kind: OutOfOrderSubStateKind.Idle
                                    }
                                }));
                                break;
                            case "QuantityUpdateNotAllowed":
                                // what do we do here ?
                                break;
                            case "Unknown":
                                setState((prev) => ({
                                    ...(prev as Scan),
                                    cart,
                                    subState: {
                                        kind: ScanSubStateKind.Error,
                                        reason: error
                                    }
                                }));
                                break;
                            default:
                                return error satisfies never;
                        }
                    }
                }, action.product.id);

                return;
            }

            case ActionKind.DeleteLineItem: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Loading }
                });

                const currentQuantity = scanState.cart.positions.reduce(
                    (acc, cur) =>
                        acc +
                        (cur.kind === "single" && cur.id === action.product.id
                            ? cur.quantity
                            : 0),
                    0
                );

                try {
                    const cart = scanState.cart.positions.some(
                        (p) =>
                            p.kind === "group" &&
                            p.lineItems.some(
                                (li) => li.id === action.product.id
                            )
                    )
                        ? await cartClient.updateLineItems([
                              {
                                  id: action.product.id,
                                  quantity:
                                      currentQuantity -
                                      (scanState.cart.positions.find(
                                          (p) =>
                                              p.kind === "single" &&
                                              p.id === action.product.id
                                      )?.quantity ?? 0)
                              }
                          ])
                        : await cartClient.deleteLineItems([action.product.id]);

                    setState({
                        ...scanState,
                        cart,
                        subState: { kind: ScanSubStateKind.Idle }
                    });
                    tracker.track("remove_from_cart", { ...action.product });
                } catch (e) {
                    const error = e as
                        | UpdateLineItemError
                        | DeleteLineItemError;
                    switch (error.case) {
                        case "SCODisabled":
                            setState((prev) => ({
                                ...prev,
                                kind: RootStateKind.OutOfOrder,
                                subState: {
                                    kind: OutOfOrderSubStateKind.Idle
                                }
                            }));
                            break;
                        case "QuantityUpdateNotAllowed":
                            // what do we do here ?
                            break;
                        case "Unknown":
                            setState((prev) => ({
                                ...(prev as Scan),
                                subState: {
                                    kind: ScanSubStateKind.Error,
                                    reason: error
                                }
                            }));
                            break;
                        default:
                            return error satisfies never;
                    }
                }
                return;
            }

            case ActionKind.DeleteCombo: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Loading }
                });

                const updates = action.position.lineItems.map((lineItem) => {
                    const currentQuantity = scanState.cart.positions
                        .flatMap((p) => {
                            switch (p.kind) {
                                case "single":
                                    return [p];
                                case "group":
                                    return p.lineItems;
                            }
                        })
                        .reduce(
                            (acc, cur) =>
                                acc +
                                (cur.id === lineItem.id ? cur.quantity : 0),
                            0
                        );
                    const newQuantity = currentQuantity - lineItem.quantity;
                    return { id: lineItem.id, quantity: newQuantity };
                });

                try {
                    const cart = await cartClient.deleteCombo(updates);

                    setState({
                        ...scanState,
                        cart,
                        subState: { kind: ScanSubStateKind.Idle }
                    });
                    tracker.track("remove_from_cart", {
                        type: "combo",
                        ids: action.position.lineItems
                            .map((li) => li.id)
                            .join(",")
                    });
                } catch (e) {
                    const error = e as UpdateLineItemError;
                    switch (error.case) {
                        case "SCODisabled":
                            setState((prev) => ({
                                ...prev,
                                kind: RootStateKind.OutOfOrder,
                                subState: {
                                    kind: OutOfOrderSubStateKind.Idle
                                }
                            }));
                            break;
                        case "QuantityUpdateNotAllowed":
                            // what do we do here ?
                            break;
                        case "Unknown":
                            setState((prev) => ({
                                ...(prev as Scan),
                                subState: {
                                    kind: ScanSubStateKind.Error,
                                    reason: error
                                }
                            }));
                            break;
                        default:
                            return error satisfies never;
                    }
                }

                return;
            }

            case ActionKind.ShowKitchenProductSelector: {
                const scanState = requireState(state, RootStateKind.Scan);
                const kitchenSubState = requireSubState(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.Kitchen
                );

                setState({
                    ...scanState,
                    subState: {
                        ...kitchenSubState,
                        subState: {
                            kind: "productSelector",
                            product: action.product
                        }
                    }
                });

                tracker.track("select_kitchen_product", {
                    productBarcode: action.product.barcode
                });

                return;
            }

            case ActionKind.HideKitchenProductSelector: {
                const scanState = requireState(state, RootStateKind.Scan);
                const kitchenSubState = requireSubState(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.Kitchen
                );

                setState({
                    ...scanState,
                    subState: {
                        ...kitchenSubState,
                        subState: {
                            kind: "idle"
                        }
                    }
                });

                return;
            }

            case ActionKind.AddKitchenProduct: {
                requireState(state, RootStateKind.Scan);
                requireSubState(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.Kitchen
                );

                setState((prev) => ({
                    ...(prev as Scan),
                    subState: {
                        ...(prev.subState as Kitchen),
                        subState: {
                            kind: "loading"
                        }
                    }
                }));

                const eventPayload = {
                    productId: action.productId,
                    barcode: action.barcode,
                    quantity: action.quantity,
                    upsellingProductBarcodes:
                        action.upsellingProductBarcodes.join(",")
                };

                try {
                    const products = [
                        {
                            barcode: action.barcode,
                            quantity: action.quantity,
                            fromKitchen: true,
                            addOnOptionIds: action.addOnOptionIds
                        },
                        ...action.comboBarcodes.map((barcode) => ({
                            barcode,
                            fromKitchen: true
                        })),
                        ...action.upsellingProductBarcodes.map((barcode) => ({
                            barcode,
                            fromKitchen: true
                        }))
                    ];
                    const cart = await cartClient.add(products);

                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage message={t("productAdded")} />
                            ),
                            timeoutMs: 2500,
                            type: "toast"
                        }
                    });

                    setState((prev) => ({
                        ...(prev as Scan),
                        cart,
                        subState: {
                            ...(prev.subState as Kitchen),
                            subState: {
                                kind: "idle"
                            }
                        }
                    }));

                    tracker.track("add_to_cart", eventPayload);
                } catch (e) {
                    const error = e as AddProductError;

                    switch (error.case) {
                        case "ProductNotFound":
                            tracker.track("product_not_found", eventPayload);

                            setState((prev) => ({
                                ...(prev as Scan),
                                subState: {
                                    ...(prev.subState as Kitchen),
                                    subState: {
                                        kind: "error" as const,
                                        reason: error
                                    }
                                }
                            }));

                            break;
                        case "SCODisabled":
                            setState((prev) => ({
                                ...prev,
                                kind: RootStateKind.OutOfOrder,
                                subState: {
                                    kind: OutOfOrderSubStateKind.Idle
                                }
                            }));

                            break;
                        case "AgeRequirementNotMet":
                        case "IdScanRequired":
                            throw e;
                        case "Unknown":
                        case "CouponAlreadyRedeemed":
                        case "ServiceNotSellable":
                        case "CouponNotApplicable": // TEMP basel-opening
                            setState((prev) => ({
                                ...(prev as Scan),
                                subState: {
                                    kind: ScanSubStateKind.Error,
                                    reason: error
                                }
                            }));
                            break;
                        default:
                            return error satisfies never;
                    }
                }

                return;
            }

            case ActionKind.ShowOperatorBadgeVerification: {
                const { kind } = requireOneOfStates(
                    state,
                    RootStateKind.OutOfOrder,
                    RootStateKind.Welcome
                );

                setState((prev) => ({
                    ...prev,
                    ...(kind === RootStateKind.Welcome
                        ? {
                              kind: RootStateKind.Welcome,
                              subState: {
                                  kind: WelcomeSubStateKind.OperatorVerification
                              }
                          }
                        : {
                              kind: RootStateKind.OutOfOrder,
                              subState: {
                                  kind: OutOfOrderSubStateKind.OperatorVerification
                              }
                          })
                }));

                return;
            }

            case ActionKind.HideOperatorBadgeVerification: {
                const { kind } = requireOneOfStates(
                    state,
                    RootStateKind.OutOfOrder,
                    RootStateKind.Welcome
                );

                setState((prev) => ({
                    ...prev,
                    ...(kind === RootStateKind.Welcome
                        ? {
                              kind: RootStateKind.Welcome,
                              subState: {
                                  kind: WelcomeSubStateKind.Idle
                              }
                          }
                        : {
                              kind: RootStateKind.OutOfOrder,
                              subState: {
                                  kind: OutOfOrderSubStateKind.Idle
                              }
                          })
                }));
                return;
            }

            case ActionKind.ScanOperatorBadge:
                await logClient.info(
                    `Operator badge scan attempted, received this code from the scanner: '${action.scannedBadge.code}'`
                );

                return setState(
                    await scanOperatorBadge(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );

            case ActionKind.BackToOperatorModeMenu:
                requireState(state, RootStateKind.Operator);
                return setState({
                    ...state,
                    kind: RootStateKind.Operator,
                    subState: { kind: OperatorSubStateKind.Idle }
                });

            case ActionKind.ShowOrders:
                return setState(
                    await showOrders(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );
            case ActionKind.LoadMoreOrders:
                return setState(
                    await loadMoreOrders(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );

            case ActionKind.ShowInsights:
                return setState(
                    await showInsights(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );

            case ActionKind.ShowHardware:
                return setState(
                    await showHardware(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );

            case ActionKind.ShowSystemInformation:
                return setState(
                    await showSystemInformation(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );

            case ActionKind.ShowConfiguration:
                return setState(
                    await showConfiguration(
                        state,
                        action,
                        client,
                        i18n.language,
                        printer
                    )
                );

            case ActionKind.ShowProducts: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: {
                        kind: ScanSubStateKind.ProductPicker,
                        productPreset: action.productPreset
                    }
                });
                tracker.track("page_view", { title: "fixtasten" });
                return;
            }

            case ActionKind.HideProducts: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Idle }
                });
                tracker.track("page_view", { title: "scan" });
                return;
            }

            case ActionKind.ShowKitchenMenu: {
                const scanState = requireState(state, RootStateKind.Scan);

                if (
                    scanState.kitchen.hasKitchen &&
                    scanState.kitchen.menu &&
                    action.code === undefined
                ) {
                    tracker.track("page_view", { title: "kitchen" });

                    setState({
                        ...scanState,
                        subState: {
                            kind: ScanSubStateKind.Kitchen,
                            subState: { kind: "idle" }
                        }
                    });
                    return;
                }

                if (
                    scanState.kitchen.hasKitchen &&
                    scanState.kitchen.menu &&
                    action.code
                ) {
                    tracker.track("page_view", { title: "kitchen" });

                    const product = scanState.kitchen.menu.categories
                        .flatMap((c) => c.products)
                        .find((p) => p.barcode === action.code);

                    if (!product) {
                        console.error(
                            "Could not open kitchen article for barcode",
                            action.code
                        );

                        setState({
                            ...scanState,
                            subState: {
                                kind: ScanSubStateKind.Kitchen,
                                subState: { kind: "idle" }
                            }
                        });
                        return;
                    }

                    setState({
                        ...scanState,
                        subState: {
                            kind: ScanSubStateKind.Kitchen,
                            subState: {
                                kind: "productSelector",
                                product
                            }
                        }
                    });

                    return;
                }

                setState({
                    ...scanState,
                    kind: RootStateKind.Error,
                    subState: { kind: "" }
                });
                return;
            }

            case ActionKind.HideKitchenMenu: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Idle }
                });
                tracker.track("page_view", { title: "kitchen" });
                return;
            }

            case ActionKind.ScanOperatorBadgeCartCheck: {
                const scanState = requireState(state, RootStateKind.Scan);
                requireSubState(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.CartCheck
                );

                await cartClient
                    .operatorScannedCartCheck()
                    .catch((e) =>
                        logClient.info(
                            `operatorScannedCartCheck call failed for ${
                                scanState.cart.id
                            }: ${JSON.stringify(e)}`
                        )
                    );

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Idle }
                });
                return;
            }

            case ActionKind.ShowCheckoutDialog: {
                const scanState = requireState(state, RootStateKind.Scan);
                requireSubState(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.Idle
                );

                setState({
                    ...scanState,
                    subState: {
                        kind: hasUnappliedCoupon(scanState.cart)
                            ? ScanSubStateKind.UnappliedCoupon
                            : ScanSubStateKind.CheckingOut
                    }
                });
                return;
            }

            case ActionKind.HideCheckoutDialog: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Idle }
                });
                return;
            }

            case ActionKind.ConfirmUnappliedCoupon: {
                const scanState = requireState(state, RootStateKind.Scan);
                requireSubState(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.UnappliedCoupon
                );

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.CheckingOut }
                });
                return;
            }

            case ActionKind.StartPayment: {
                const scanState = requireState(state, RootStateKind.Scan);
                requireOneOfSubStates(
                    state,
                    RootStateKind.Scan,
                    ScanSubStateKind.CheckingOut,
                    ScanSubStateKind.CheckoutError
                );

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.ProcessPayment }
                });

                try {
                    const totalPrice = `${scanState.cart.totalPrice.currency} ${scanState.cart.totalPrice.value}`;
                    tracker.track("begin_checkout", { totalPrice });

                    const { orderId, userId, kitchenOrder } =
                        await cartClient.checkout(
                            i18n.language,
                            scanState.simulatePaymentResponse
                        );

                    setState({
                        ...state,
                        orderId,
                        userId,
                        kitchenOrder,
                        kind: RootStateKind.CheckoutSuccess,
                        subState: { kind: CheckoutSuccessSubStateKind.Idle }
                    });

                    if (kitchenOrder) {
                        dispatch({
                            kind: ActionKind.PrintKitchenReceipt,
                            kitchenOrderId: kitchenOrder.id
                        });
                    }

                    tracker.track("checkout_success", { totalPrice });
                    tracker.track("page_view", { title: "end" });
                    return;
                } catch (e) {
                    await logClient.info(
                        `${getEzioId()} checkout failure ${JSON.stringify(e)}`
                    );
                    const error = e as CheckoutError;
                    tracker.track("checkout_failure", {
                        reason: error?.case
                    });

                    switch (error?.case) {
                        case "Aborted":
                            setState({
                                ...scanState,
                                subState: {
                                    kind: ScanSubStateKind.CheckoutError,
                                    reason: error
                                }
                            });
                            return;

                        case "CartCheckRequired":
                            setState({
                                ...scanState,
                                subState: { kind: ScanSubStateKind.CartCheck }
                            });
                            return;

                        default:
                            setState({
                                ...scanState,
                                subState: {
                                    kind: ScanSubStateKind.CheckoutError,
                                    reason: error
                                }
                            });
                            return;
                    }
                }
            }

            case ActionKind.ChangeLanguage: {
                await i18n.changeLanguage(action.code);
                tracker.setTrackingProperties({ language: action.code });
                tracker.track("select_content", { language: action.code });
                return;
            }

            case ActionKind.HideCartErrorDialog: {
                const scanState = requireState(state, RootStateKind.Scan);

                if (scanState.subState.kind === ScanSubStateKind.Kitchen) {
                    setState({
                        ...scanState,
                        subState: {
                            ...scanState.subState,
                            subState: {
                                kind: "idle"
                            }
                        }
                    });
                } else {
                    setState({
                        ...scanState,
                        subState: { kind: ScanSubStateKind.Idle }
                    });
                }
                return;
            }

            case ActionKind.SendFeedback: {
                requireState(state, RootStateKind.CheckoutSuccess);

                await postCheckoutClient.sendFeedback(action.rate);
                return;
            }

            case ActionKind.StartGetReceipt: {
                const checkoutSuccessState = requireState(
                    state,
                    RootStateKind.CheckoutSuccess
                );

                setState({
                    ...checkoutSuccessState,
                    subState: {
                        kind: CheckoutSuccessSubStateKind.ReceiptLoading
                    }
                });
                const receiptResponse = await orderClient.getReceiptUrl(
                    action.orderId,
                    i18n.language
                );
                setState({
                    ...checkoutSuccessState,
                    subState: {
                        kind: CheckoutSuccessSubStateKind.ReceiptSuccess,
                        receiptUrl: receiptResponse.receiptUrl
                    }
                });
                tracker.track("receipt_requested");
                return;
            }

            case ActionKind.HideIdScan: {
                const scanState = requireState(state, RootStateKind.Scan);

                setState({
                    ...scanState,
                    subState: { kind: ScanSubStateKind.Idle }
                });
                return;
            }

            case ActionKind.FinalizeSession: {
                reset();
                return;
            }

            case ActionKind.Reset: {
                void logClient.info("Reset action: Begin");

                setState({
                    ...welcomeRootState,
                    subState: { kind: WelcomeSubStateKind.Resetting }
                });

                // Remote reset
                const shouldAbortPayment =
                    state.kind === RootStateKind.Scan &&
                    state.subState.kind === ScanSubStateKind.ProcessPayment;
                await postCheckoutClient.reset(
                    shouldAbortPayment,
                    defaultLanguage
                );

                reset();

                void logClient.info("Reset action: End");
                return;
            }

            case ActionKind.ToggleAccessibilityMode: {
                requireOneOfStates(
                    state,
                    RootStateKind.OutOfOrder,
                    RootStateKind.Welcome,
                    RootStateKind.Scan,
                    RootStateKind.Operator,
                    RootStateKind.CheckoutSuccess
                );

                tracker.track("toggle_accessibility_mode", {
                    state: !state.accessibilityMode
                });
                setState({
                    ...state,
                    accessibilityMode: !state.accessibilityMode
                });
                return;
            }
            case ActionKind.Flap: {
                requireState(state, RootStateKind.Scan);
                tracker.track("flappy_found");
                setState({
                    ...state,
                    kind: RootStateKind.Flappy,
                    subState: { kind: FlappySubStateKind.Playing }
                });
                return;
            }

            case ActionKind.PrintReceipt: {
                requireOneOfStates(
                    state,
                    RootStateKind.Scan,
                    RootStateKind.CheckoutSuccess,
                    RootStateKind.Operator
                );

                if (!import.meta.env.DEV && !connected) {
                    await connect();
                }
                const content = await orderClient.getReceipt(
                    action.orderId,
                    i18n.language
                );
                await print(content);

                tracker.track("receipt_printed", {
                    orderId: action.orderId
                });
                return;
            }

            case ActionKind.PrintKitchenReceipt: {
                requireOneOfStates(
                    state,
                    RootStateKind.Scan,
                    RootStateKind.CheckoutSuccess,
                    RootStateKind.Operator
                );

                if (!import.meta.env.DEV && !connected) {
                    await connect();
                }
                const content = await orderClient.getKitchenReceipt(
                    i18n.language,
                    action.kitchenOrderId
                );
                await print(content);

                tracker.track("kitchen_receipt_printed", {
                    orderId: action.kitchenOrderId
                });
                return;
            }

            case ActionKind.TogglePaymentSimulator: {
                const scanState = requireState(state, RootStateKind.Scan);

                const { simulatePaymentResponse } = scanState;
                const newValue =
                    simulatePaymentResponse === "failure"
                        ? "success"
                        : "failure";

                return setState({
                    ...scanState,
                    simulatePaymentResponse: newValue
                });
            }

            case ActionKind.ShowMessage: {
                const newId = self.crypto.randomUUID();

                setTimeout(() => {
                    setState((prev) => ({
                        ...prev,
                        messages: prev.messages.filter(({ id }) => id !== newId)
                    }));
                }, action.message.timeoutMs);

                return setState((prev) => ({
                    ...prev,
                    messages: prev.messages.concat([
                        { ...action.message, id: newId }
                    ])
                }));
            }

            case ActionKind.SetScoStatus: {
                await configClient.setStatus({ isEnabled: action.isEnabled });
                window.location.reload();
                return;
            }

            case ActionKind.SetCartCheck: {
                await configClient.setCartCheck({
                    isEnabled: action.isEnabled
                });
                window.location.reload();
                return;
            }

            case ActionKind.PaymentDeviceLogin: {
                try {
                    await configClient.paymentDeviceLogin();
                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage
                                    message={t(
                                        "operator.hardware.paymentDeviceConfig.loginSucceeded"
                                    )}
                                />
                            ),
                            timeoutMs: 2500,
                            type: "toast"
                        }
                    });
                } catch (e) {
                    const reason = (e as globalThis.Error).message;

                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage
                                    variant="error"
                                    message={t(
                                        "operator.hardware.paymentDeviceConfig.loginFailed",
                                        { reason }
                                    )}
                                />
                            ),
                            timeoutMs: 2500,
                            type: "toast"
                        }
                    });
                }
                return;
            }

            case ActionKind.ProbePaymentTerminal: {
                try {
                    await configClient.probePaymentTerminal();
                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage
                                    message={t(
                                        "operator.hardware.paymentDeviceConfig.probeSucceeded"
                                    )}
                                />
                            ),
                            timeoutMs: 2500,
                            type: "toast"
                        }
                    });
                } catch {
                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage
                                    variant="error"
                                    message={t(
                                        "operator.hardware.paymentDeviceConfig.probeFailed"
                                    )}
                                />
                            ),
                            timeoutMs: 2500,
                            type: "toast"
                        }
                    });
                }
                return;
            }

            case ActionKind.UpdatePaymentDeviceIp: {
                try {
                    await configClient.updatePaymentDeviceIp(action.ip);
                } catch (e) {
                    const reason = (e as globalThis.Error).message;

                    dispatch({
                        kind: ActionKind.ShowMessage,
                        message: {
                            content: (
                                <ToastMessage
                                    variant="error"
                                    message={t(
                                        "operator.hardware.paymentDeviceConfig.ipUpdateFailed",
                                        { reason }
                                    )}
                                />
                            ),
                            timeoutMs: 2500,
                            type: "toast"
                        }
                    });

                    return false;
                }

                dispatch({
                    kind: ActionKind.ShowMessage,
                    message: {
                        content: (
                            <ToastMessage
                                message={t(
                                    "operator.hardware.paymentDeviceConfig.ipUpdated"
                                )}
                            />
                        ),
                        timeoutMs: 2500,
                        type: "toast"
                    }
                });

                // The IP is already persisted. A failing login must never
                // undo the success result, so isolate it from the return value.
                if (store.country === germanyCountryCode) {
                    await dispatch({
                        kind: ActionKind.PaymentDeviceLogin
                    }).catch(() => undefined);
                }

                return true;
            }

            default:
                return action satisfies never;
        }
    };

    const dispatch = actionHandler(client);

    return (
        <StateContext.Provider value={{ dispatch, state }}>
            {state.accessibilityMode && (
                <div className="h-2/6 flex-none bg-black"></div>
            )}
            {children}

            {accessibilityModeAvailable && (
                <div
                    className={`flex justify-center ${
                        state.kind === RootStateKind.Operator
                            ? "fixed inset-x-0 bottom-0 z-40"
                            : ""
                    }`}
                >
                    <Button
                        className="relative flex h-11 w-24 items-center justify-center rounded-tl-full rounded-tr-full bg-button-subtle-background text-center"
                        onClick={() =>
                            dispatch({
                                kind: ActionKind.ToggleAccessibilityMode
                            })
                        }
                    >
                        <div className="text-secondary-500 [&>*]:w-48">
                            {state.accessibilityMode
                                ? svg.expand
                                : svg.wheelchair}
                        </div>
                    </Button>
                </div>
            )}
        </StateContext.Provider>
    );
};
